import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ChatAttachmentView, ChatDialogEvent, spacesCorrection } from 'sdk';
import { linksGen } from 'sdk';

import { useSDK } from '../../../../app';
import { styled } from '../../../../styled';
import type { Theme } from '../../../../theme/interface/detailed';
import { useSanitize } from '../../../../utils/useSanitize';
import { ReplyMessageScope } from '../../../forms/fields/messageField/reply/ReplyMessageScope';
import { AttachmentPreview } from '../attachmentPreview';
import { Message } from '../message';
import { MessageContent } from '../messageContent';
import { MessageTime } from '../messageTime';
import { StatusWrapper } from '../statusWrapper';
import { MessageDeliveredIcon } from './messageDeliveredIcon';
import { MessageReadIcon } from './messageReadIcon';
import { PendingMessageIcon } from './pendingMessageIcon';

type Interface = {
	message: ChatDialogEvent;
	className?: string;
	attachmentId?: number;
	Component: (v: { message: ChatDialogEvent }) => ReactElement | null;
};

const styles = css`
	clear: both;
	display: inline-block;
	float: right;
	max-width: 85%;
	padding-bottom: 8px;
`;

const Component = observer(({ message, className, Component }: Interface): ReactElement => {
	const { eventsStore } = useSDK();

	return (
		<div id={`ncc-message-${message.id}`} className={className}>
			<MessageContent>
				<Message message={message} fromClient>
					{message.replyTo && <ReplyMessageScope fromClient message={message.replyTo} />}
					<Component message={message} />
					<StatusWrapper>
						<MessageTime fromClient date={message.creationTimestamp} />
						{eventsStore.isDeliveredToOperator(message) ? (
							eventsStore.isReadByOperator(message) ? (
								<MessageReadIcon />
							) : (
								<MessageDeliveredIcon />
							)
						) : (
							<PendingMessageIcon />
						)}
					</StatusWrapper>
				</Message>
			</MessageContent>
		</div>
	);
});

const ClientMessageBase = styled(Component)(
	({
		theme: {
			chat: {
				messages: { messageContainer },
			},
		},
	}: {
		theme: Theme;
	}) => messageContainer,
	styles
);

const Text = ({ message }: { message: ChatDialogEvent }): ReactElement | null => {
	const fixedMessage = useSanitize(message.view.text ? spacesCorrection(linksGen(message.view.text)) : '');
	return fixedMessage ? <div dangerouslySetInnerHTML={fixedMessage} /> : null;
};

const Attachment = ({ message }: { message: ChatDialogEvent }): ReactElement | null => {
	const attachmentId = (message.view as ChatAttachmentView).attachId;
	return attachmentId ? <AttachmentPreview fromClient id={attachmentId} /> : null;
};

const ClientMessageText = ({ message }: { message: ChatDialogEvent }): ReactElement => (
	<ClientMessageBase message={message} Component={Text} />
);

const ClientMessageAttachment = ({ message }: { message: ChatDialogEvent }): ReactElement => (
	<ClientMessageBase message={message} Component={Attachment} />
);

export const ClientMessage = ({ message }: { message: ChatDialogEvent }): ReactElement => {
	if (message.view.text && (message.view as ChatAttachmentView).attachId) {
		return (
			<div>
				<ClientMessageText message={message} />
				<ClientMessageAttachment message={message} />
			</div>
		);
	}
	if (message.view.text) return <ClientMessageText message={message} />;

	return <ClientMessageAttachment message={message} />;
};
